import {
    getAxiosFulfilledState,
    getPendingState,
    getRejectedState,
    getSettingsState,
    resetState,
} from '../helpers/reducerHelper'
import { IReduxAction } from './index'
import { FileAction, FileMutationDto, IFileDto, IOverviewItemDto } from '../config/fileConfig'
import { FileListItemDto } from '../context/FileContext'
import { NoteAction, NoteOverviewDto } from '../config/noteConfig'
import { TaskAction, TaskOverviewDto } from '../config/taskConfig'
import { TimeAction } from '../config/registration/timeConfig'
import { ActionType } from 'redux-promise-middleware'
import { IFileListSettings } from '../components/List/File/FileList'
import { TimeOverviewDto } from '../config/registrationConfig'
import { Message } from '@microsoft/microsoft-graph-types'
import { DataPromise, getDataEmptyState, IGraphArrayPayload } from '../context/AxiosContext'

export interface IFileState {
    overview: DataPromise<IOverviewItemDto[], IFileListSettings>
    list: DataPromise<FileListItemDto[]>
    current: DataPromise<IFileDto>
    mutation: DataPromise<FileMutationDto>
    emails: DataPromise<IGraphArrayPayload<Message>>
    notes: DataPromise<NoteOverviewDto[]>
    tasks: DataPromise<TaskOverviewDto[]>
    times: DataPromise<TimeOverviewDto[]>
    suggestedReference?: string
}

const defaultState: IFileState = {
    overview: getDataEmptyState<IOverviewItemDto[], IFileListSettings>({
        query: undefined,
        minQueryLength: 3,
        matchColumns: ['name', 'reference'],
        filter: {
            statuses: undefined,
            ownerUsers: undefined,
            executiveUsers: undefined,
        },
    }),
    list: getDataEmptyState<FileListItemDto[]>(undefined, []),
    current: getDataEmptyState<IFileDto>(),
    mutation: getDataEmptyState<FileMutationDto>(),
    emails: getDataEmptyState<IGraphArrayPayload<Message>>(),
    notes: getDataEmptyState<NoteOverviewDto[]>(undefined, []),
    tasks: getDataEmptyState<TaskOverviewDto[]>(),
    times: getDataEmptyState<TimeOverviewDto[]>(),
    suggestedReference: null,
}

export default function reducer(state: IFileState = defaultState, action: IReduxAction): IFileState {
    switch (action.type) {
        case FileAction.OverviewSettings: {
            return getSettingsState(state, action, 'overview')
        }
        case `${FileAction.GetOverview}_${ActionType.Pending}`: {
            return getPendingState(state, 'overview')
        }
        case `${FileAction.GetOverview}_${ActionType.Rejected}`: {
            return getRejectedState(state, action, 'overview')
        }
        case `${FileAction.GetOverview}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'overview')
        }
        case `${FileAction.GetList}_${ActionType.Pending}`: {
            return getPendingState(state, 'list')
        }
        case `${FileAction.GetList}_${ActionType.Rejected}`: {
            return getRejectedState(state, action, 'list')
        }
        case `${FileAction.GetList}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'list')
        }
        case FileAction.ResetList: {
            // Reset the state to force other components to update the data.
            return resetState(state, { list: null })
        }
        case `${FileAction.Get}_${ActionType.Pending}`: {
            return getPendingState(state, 'current')
        }
        case `${FileAction.Get}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'current')
        }
        case `${FileAction.GetMutation}_${ActionType.Pending}`: {
            return getPendingState(state, 'mutation')
        }
        case `${FileAction.GetMutation}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'mutation')
        }
        case `${FileAction.GetEmails}_${ActionType.Pending}`: {
            return getPendingState(state, 'emails')
        }
        case `${FileAction.GetEmails}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'emails')
        }
        case `${FileAction.GetEmails}_${ActionType.Rejected}`: {
            return getRejectedState(state, action, 'emails')
        }
        case `${FileAction.GetNotes}_${ActionType.Pending}`: {
            return getPendingState(state, 'notes')
        }
        case `${FileAction.GetNotes}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'notes')
        }
        case `${FileAction.GetTasks}_${ActionType.Pending}`: {
            return getPendingState(state, 'tasks')
        }
        case `${FileAction.GetTasks}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'tasks')
        }
        case `${FileAction.GetTimes}_${ActionType.Pending}`: {
            return getPendingState(state, 'times')
        }
        case `${FileAction.GetTimes}_${ActionType.Fulfilled}`: {
            return getAxiosFulfilledState(state, action, 'times')
        }
        case `${FileAction.Add}_${ActionType.Fulfilled}`:
        case `${FileAction.Update}_${ActionType.Fulfilled}`:
        case `${FileAction.Delete}_${ActionType.Fulfilled}`: {
            // Reset the state to force other components to update the data.
            return resetState<IFileState>(state, {
                overview: null,
                list: [],
                complete: null,
            })
        }
        case FileAction.ResetCurrent: {
            return resetState(state, {
                current: null,
                emails: null,
                tasks: null,
                times: null,
                notes: null,
            })
        }
        case FileAction.ResetOverview: {
            // Reset the state to force other components to update the data.
            return resetState(state, { overview: null })
        }
        case FileAction.ResetEmails: {
            return resetState(state, { emails: null })
        }
        case `${NoteAction.Add}_${ActionType.Fulfilled}`:
        case `${NoteAction.Update}_${ActionType.Fulfilled}`:
        case `${NoteAction.Delete}_${ActionType.Fulfilled}`:
        case FileAction.ResetNotes: {
            return resetState(state, { notes: null })
        }
        case `${TaskAction.Add}_${ActionType.Fulfilled}`:
        case `${TaskAction.Update}_${ActionType.Fulfilled}`:
        case `${TaskAction.Delete}_${ActionType.Fulfilled}`:
        case FileAction.ResetTasks: {
            return resetState(state, { tasks: null })
        }
        case `${TimeAction.Add}_${ActionType.Fulfilled}`:
        case `${TimeAction.Update}_${ActionType.Fulfilled}`:
        case `${TimeAction.Delete}_${ActionType.Fulfilled}`:
        case FileAction.ResetTimes: {
            return resetState(state, { times: null })
        }
    }

    return state
}
